import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Film, Clock, Globe, CheckCircle2, Lock, Send } from "lucide-react";
import { toast } from "sonner";

export default function SubmitFilm() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    title: "",
    synopsis: "",
    director_name: "",
    email: "",
    phone_number: "",
    country: "",
    film_link: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get("/submissions/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const required = ["title", "synopsis", "director_name", "email", "phone_number", "country", "film_link"];
    for (const f of required) {
      if (!form[f] || !form[f].trim()) {
        toast.error("Please complete all fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post("/submissions/submit", form);
      setConfirmed({ title: form.title, director: form.director_name, email: form.email });
      toast.success("Submission received. Check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isClosed = status && !status.is_open;

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <p className="text-xs uppercase tracking-[0.3em] text-terracotta font-semibold mb-3 flex items-center gap-2">
          <Film className="w-3 h-3" /> Launch-Night Submissions
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] mb-4" data-testid="submit-title">
          Submit your short film for the <span className="text-terracotta">Cinevoya Launch</span>
        </h1>
        <p className="text-foreground/70 leading-relaxed max-w-2xl">
          Your film could screen at Silverbird Galleria on 13 November 2026. Selected directors get comp tickets for their team,
          Cinevoya distribution, and a spotlight feature on the platform.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className={`inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-semibold ${
            isClosed ? "bg-red-500/15 text-red-300 border border-red-500/30" : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
          }`} data-testid="submissions-status-pill">
            <Clock className="w-3 h-3" />
            {isClosed ? "Submissions closed" : `Submissions close ${status?.deadline_display || "31 October 2026"}`}
          </div>
        </div>

        <div className="rounded-2xl bg-surface border border-white/10 p-6 sm:p-10 mt-10" data-testid="submit-form-card">
          {confirmed ? (
            <div className="text-center py-6" data-testid="submit-confirmed">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">Submission received</h2>
              <p className="text-foreground/70 leading-relaxed">
                Thanks, <strong className="text-foreground/95">{confirmed.director}</strong>. We&apos;ve confirmed &ldquo;<strong className="text-terracotta">{confirmed.title}</strong>&rdquo; and sent a note to{" "}
                <strong className="text-foreground/95">{confirmed.email}</strong>. Our curation team reviews every entry — you&apos;ll hear back either way by early November.
              </p>
              <Button
                onClick={() => { setConfirmed(null); setForm({ title: "", synopsis: "", director_name: "", email: "", phone_number: "", country: "", film_link: "" }); }}
                variant="outline"
                className="mt-6 border-white/15 hover:bg-white/5"
                data-testid="submit-another-btn"
              >
                Submit another film
              </Button>
            </div>
          ) : isClosed ? (
            <div className="text-center py-6" data-testid="submit-closed">
              <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-red-400" />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">Submissions are closed</h2>
              <p className="text-foreground/70">
                The window for this call ended on 31 October 2026. Follow us for the next open call.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="submit-form">
              <div>
                <label htmlFor="sub-title" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Film title</label>
                <Input
                  id="sub-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Harmattan Skies"
                  className="bg-ink border-white/10 mt-1.5"
                  required
                  data-testid="sub-title-input"
                />
              </div>

              <div>
                <label htmlFor="sub-synopsis" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Synopsis</label>
                <Textarea
                  id="sub-synopsis"
                  value={form.synopsis}
                  onChange={(e) => setForm({ ...form, synopsis: e.target.value.slice(0, 2000) })}
                  placeholder="A short paragraph — what happens, who it's about, why it matters."
                  className="bg-ink border-white/10 mt-1.5 min-h-[120px] resize-none"
                  required
                  data-testid="sub-synopsis-input"
                />
                <p className="text-[10px] text-foreground/40 mt-1 text-right">{form.synopsis.length} / 2000</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="sub-director" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Director name</label>
                  <Input
                    id="sub-director"
                    value={form.director_name}
                    onChange={(e) => setForm({ ...form, director_name: e.target.value })}
                    placeholder="Full name"
                    className="bg-ink border-white/10 mt-1.5"
                    required
                    data-testid="sub-director-input"
                  />
                </div>
                <div>
                  <label htmlFor="sub-country" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold flex items-center gap-1"><Globe className="w-3 h-3" /> Country</label>
                  <Input
                    id="sub-country"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="Nigeria, Ghana, Kenya…"
                    className="bg-ink border-white/10 mt-1.5"
                    required
                    data-testid="sub-country-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="sub-email" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Email</label>
                  <Input
                    id="sub-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="director@example.com"
                    className="bg-ink border-white/10 mt-1.5"
                    required
                    data-testid="sub-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="sub-phone" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Phone</label>
                  <Input
                    id="sub-phone"
                    value={form.phone_number}
                    onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                    placeholder="+234 801 234 5678"
                    className="bg-ink border-white/10 mt-1.5"
                    required
                    data-testid="sub-phone-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="sub-link" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Film link (Google Drive or Vimeo)</label>
                <Input
                  id="sub-link"
                  type="url"
                  value={form.film_link}
                  onChange={(e) => setForm({ ...form, film_link: e.target.value })}
                  placeholder="https://vimeo.com/… or https://drive.google.com/…"
                  className="bg-ink border-white/10 mt-1.5"
                  required
                  data-testid="sub-link-input"
                />
                <p className="text-[11px] text-foreground/40 mt-1">
                  Make sure the link is set to publicly viewable (anyone with the link).
                </p>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-terracotta hover:bg-ember text-white font-semibold h-11"
                data-testid="sub-submit-btn"
              >
                <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit for consideration"}
              </Button>
              <p className="text-[11px] text-foreground/40 text-center">
                We&apos;ll email your confirmation instantly. Curation results by early November.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
